export const STUDY_AHEAD_TIME_SECS = 1200; // 20 minutes
export const SECONDS_PER_DAY = 86400;

export enum UserStatus {
    TRIAL = "TRIAL",
    STANDARD = "STANDARD",
    SUBSCRIBED = "SUBSCRIBED"
}

export enum ProficiencyLevel {
    NOVICE = "novice",
    BEGINNER = "beginner",
    INTERMEDIATE = "intermediate",
    ADVANCED = "advanced",
}

// Version of the voice-data training consent wording the client shows.
export const TRAINING_CONSENT_VERSION = 3;

export type TrainingConsentSource = 'signup' | 'settings' | 'reconsent_prompt';

export enum AgeGroup {
    AGE_13_17 = "13_17",
    AGE_18_24 = "18_24",
    AGE_25_34 = "25_34",
    AGE_35_44 = "35_44",
    AGE_45_54 = "45_54",
    AGE_55_PLUS = "55_plus",
}

export enum TutorLanguageUsage {
    TARGET_LANGUAGE = "target_language",
    MIXED_LANGUAGE = "mixed_language",
}

export enum TutorFormalness {
    STANDARD = "standard",
    FORMAL = "formal",
    SLANG = "slang",
}

export enum StudentGrammarHelp {
    TUTOR_INITIATED = "tutor_initiated",
    STUDENT_INITIATED = "student_initiated",
}

export enum SavedWordsPracticeStyle {
    DEFINITION = "definition",
    EXAMPLE = "example"
}

export type ContentSection = {
    content: string,
    isWord: boolean,
    definition: string
}

export type Agent = {
    agentId: string;
    agentTemplateId: string;
    userId: string;
    voiceId: string;
    voiceService: string;
    agentType: string;
    name: string;
    language: string;
    bio?: string;
    gender: string;
    age: number;
    homeCity: string;
    homeCountry: string;
    timezone?: string;
    createdDate: number;
    customInstructions?: string | null;
}

export type UserSettings = {
    id: string;
    userId: string;
    username: string;
    name: string;
    sourceLang: string;
    secondSourceLanguage?: string | null;
    thirdSourceLanguage?: string | null;
    guiLang: string;
    hasOnboarded: boolean;
    userStatus: UserStatus;
    maxUsageStandard: number;
    tutorPatience: number;
    dailyMinutesGoal: number;
    speechSpeedRatio: number;
    selectedAgentTemplateId: string;
    llmMode: boolean;
    noiseCancellation: boolean;
    timezone?: string;
    stripeCustomerId?: string;
    manualMode: boolean;
    maxDailyReviews: number;
    maxDailyNewCards: number;
    enableRecognitionCards: boolean;
    enableRecallCards: boolean;
    playAudioAutomatically: boolean;
    targetLangDefinition: boolean;
    enableSpeakingMode: boolean;
    showSentenceInFront: boolean;
    lessonMode: boolean;
    autoCloseLessons: boolean;
    preferredNotificationTime?: number;
    marketingSource?: string;
    ageGroup?: AgeGroup | null;
    showRubyInFront: boolean;
    autoCreateFlashcards: boolean;
    autoFlipFlashcardsSeconds: number;
    creator: boolean;
    // Server-decided: true only on dev and local servers (in-call debug panel).
    callDebug?: boolean;
    disableContentFilter: boolean;
    showFlags: boolean;
    allowDataTraining: boolean;
    // Older servers omit these: treat as 0 (no current consent) and false.
    trainingConsentVersion?: number;
    // Opted in under the old wording and must be asked again.
    trainingConsentPrompt?: boolean;
    disableDrillSoundEffects: boolean;
    advancedTranscription: boolean;
    acceptedTermsDate?: string | null;
    attributedCampaignSlug?: string | null;
    attributedRedirectClickId?: string | null;
    attributedPlatform?: string | null;
    attributedNetwork?: string | null;
    referralInviteEnabled?: boolean;
    referralPromptReady?: boolean;
    billingIssue?: BillingIssue | null;
}

// A renewal payment failed. Older servers omit it; manageUrl is null when a Stripe
// user should be sent to the billing portal instead of an invoice.
export type BillingIssue = {
    platform: "apple" | "google" | "stripe";
    state: "grace_period" | "on_hold" | "expired";
    manageUrl: string | null;
    since: number | null;
}

export enum MarketingSource {
    FRIENDS_FAMILY = "friends_family",
    INSTAGRAM = "instagram",
    YOUTUBE = "youtube",
    TIKTOK = "tiktok",
    REDDIT = "reddit",
    AI_SEARCH = "ai_search",
    GOOGLE_SEARCH = "google_search",
    APP_STORE = "app_store",
    PLAY_STORE = "play_store",
    NEWS_BLOG = "news_blog",
    OTHER = "other",
}

export type LanguageSettings = {
    id: string;
    userId: string;
    language: string;
    proficiencyLevel: string;
    formalness: string;
    grammarCorrection: string | null;
    tutorLanguageUsage: string | null;
    savedWordsPracticeStyles: string[];
    finishedIntroLesson: boolean;
    languageStart: string | null;
    interest1: string | null;
    interest2: string | null;
    introPrompt: string | null;
    conversationPrompt: string | null;
    languageGoal1: string | null;
    languageGoal2: string | null;
    rubyInChat: boolean;
    transliterationStyle: string | null;
}

export type KickstartMessage = {
    type: string;
    kickstart_id: string;
    kickstart_type: string;
    kickstart_info: Record<string, string | number | boolean | null | undefined>;
    session_id_from_kickstart?: string;
}

export class Message {
    messageId: string;
    fromName: string;
    fromId: string;
    toId: string;
    content: ContentSection[];
    time: number;
    confirmed: boolean;
    isKickstartMessage: boolean;
    transcribing: boolean;
    // Photo the student sent during this call; rendered from a local object URL.
    imageId?: string;
    constructor(messageId: string, fromId: string, fromName: string, toId: string, content: ContentSection[], time: number, confirmed: boolean, isKickstartMessage: boolean, transcribing: boolean = false) {
        this.messageId = messageId;
        this.fromId = fromId;
        this.fromName = fromName;
        this.toId = toId;
        this.content = content;
        this.time = time;
        this.confirmed = confirmed;
        this.isKickstartMessage = isKickstartMessage;
        this.transcribing = transcribing;
    }
}

export type HistorySessionSummary = {
    sessionId: string;
    agentId: string;
    kickstartId: string;
    kickstartType: string;
    title?: string;
    firstMessageTime: number;
    lastMessageTime: number;
    duration: number;
    messageCount: number;
}

export enum CardType {
    RECALL = "RECALL",
    RECOGNITION = "RECOGNITION"
}

export enum State {
    LEARNING = 1,
    REVIEW = 2,
    RELEARNING = 3
}

export enum Rating {
    AGAIN = 1,
    HARD = 2,
    GOOD = 3,
    EASY = 4
}

export type CharacterMeaning = {
    character: string,
    meaning: string
}

export type Word = {
    id: number,
    userId: string,
    word: string,
    wordWithTransliteration: string,
    sentence: string,
    messageId: string | null,
    addedTime: number,
    language: string,
    shortDefinitionSourceLang: string,
    shortDefinitionTargetLang: string,
    longDefinitionSourceLang: string,
    longDefinitionTargetLang: string,
    partOfSpeech?: string | null,
    grammaticalGender?: string | null,
    definiteArticle?: string | null,
    imageUrl?: string | null,
    characterMeanings?: CharacterMeaning[] | null,
    sharedExampleSentence?: string | null,
    sharedExampleSentenceAudioS3Link?: string | null,
    wordAudioS3Link?: string,
    sentenceAudioS3Link?: string
}

export type Card = {
    id: number,
    wordId: number,
    cardType: CardType,
    state: State,
    step: number | null,
    stability: number | null,
    difficulty: number | null,
    due: number,
    lastReview: number | null,
    enabled: boolean
}


export type Review = {
    id: number,
    cardId: number,
    rating: Rating,
    reviewTime: number,
    reviewDuration: number
}


export type StudyStats = {
    totalCallSeconds: number,
    totalFlashcardSeconds: number,
    todayCallSeconds: number,
    todayFlashcardSeconds: number,
    totalReviews: number,
    todayReviews: number
}

export type HistoricalStats = {
    daily: Record<string, StatsRecord>,
    weekly: Record<string, StatsRecord>,
    monthly: Record<string, StatsRecord>
}

export type StatsRecord = {
    call_seconds?: number,
    flashcard_seconds?: number,
    review_count?: number
}

export const templateIdToLanguage = (templateId?: string) => {
    if (!templateId) {
        return "";
    }
    const parts = templateId.split("-");
    if (parts.length < 2) {
        return "";
    }
    return parts[0];
}

// Curriculum-related types and constants
export enum SubunitStatus {
    COMPLETED = "completed",
    CURRENT = "current",
    LOCKED = "locked"
}

export const MIN_LESSON_DURATION_SECS = 120;

export type CurriculumTimelineItem = {
    session?: Session;
    subunit?: any; // Will be Subunit from api.ts
    unit?: any; // Will be Unit from api.ts
    subunitNumber: string;
    title: string;
    status: SubunitStatus;
    isSession: boolean;
    isCurrentLesson: boolean;
    isLocked: boolean;
    isCompleted: boolean;
}

export type SessionGroup = {
    unit: any | null; // Will be Unit from api.ts or null
    sessions: Session[];
}

// Define Session as a type alias
export type Session = {
    id: string;
    userId: string;
    agentId: string;
    date: number; // Timestamp
    duration: number; // Seconds
    kickstartId: string;
    kickstartType: string;
    localizedDescription: string;
    subunitId?: string; // Optional subunit ID for curriculum tracking
}

export enum SessionType {
    CHAT = "chat",
    SHADOWING = "shadowing",
    WORD_TRANSLATION = "word_translation",
    SENTENCE_TRANSLATION = "sentence_translation",
    FILL_IN_THE_BLANK = "fill_in_the_blank",
    MIXED_DRILLS = "mixed_drills"
}

export type NewDrill = {
    id: string;
    type: string;
    recallDrill: boolean;
    targetSentence: string;
    sourceSentence: string;
    repetitions: number;
    hint?: string;
}

export type ShadowingContent = {
    content: string;
    isWord: boolean;
    userSaid: boolean;
}

export type ShadowingAnswer = {
    content: ShadowingContent[];
}

export type WordTranslationAnswer = {
    transcript: string;
    alternativeAnswer: string | null;
}

export type SentenceTranslationAnswer = {
    transcript: string;
    alternativeAnswer: string | null;
}


export type DrillAnswer = {
    userAnswer: ShadowingAnswer | WordTranslationAnswer | SentenceTranslationAnswer;
    points: number;
    repetitionComplete: boolean;
    repetitionsRemaining: number;
    wasSkipped?: boolean;
}



// Drill definitions for tap-to-translate in drills
export type DrillDefinitions = {
    drillId: string;
    content: ContentSection[];
}

export type ShadowingKickstartInfo = {
    difficulty: number;
    numDrills: number;
    repeat: number;
    useSavedWords: boolean;
}

export type WordTranslationKickstartInfo = {
    difficulty: number;
    numDrills: number;
    useSavedWords: boolean;
    enableRecall: boolean;
    enableRecognition: boolean;
}
